import React from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import GetAppIcon from '@mui/icons-material/GetApp';
import TableViewIcon from '@mui/icons-material/TableView';
import { SimpleDialog } from '../common/SimpleDialog';
import { exportMultipleChartsToPdf } from '../common/pdfUtils';
import { useFeatureFlag } from '../../utils/featureFlagApi';

interface SpeciesChildOption {
  label: string;
}

interface SpeciesOrderOption {
  label: string;
  children?: SpeciesChildOption[];
}

interface SiteOption {
  id: number;
  site_display_name: string;
  company_id?: number;
}

interface CameraOption {
  id: number;
  camera_name: string;
}

type NumberStateSetter = (value: number | ((previous: number) => number)) => void;

interface StatisticsStickyNavbarProps {
  camera: number[];
  cameraData?: { data?: CameraOption[] };
  dateRange: { startDate: Date; endDate: Date };
  dateValue?: number | null;
  listOfSites?: { data?: SiteOption[] };
  selectedOrders: string[];
  selectedSpecies: string[];
  setCamera?: (cameraIds: number[]) => void;
  setDateRange: (range: { startDate: Date; endDate: Date }) => void;
  setDateValue: NumberStateSetter;
  setSelectedOrders: (orders: string[]) => void;
  setSelectedSpecies: (species: string[]) => void;
  setSite: (siteId: string) => void;
  showDownloadButtons?: boolean;
  site: string;
  speciesList?: SpeciesOrderOption[] | null;
  updateCameraDetails?: (event: any, cameraIds: number[]) => void;
  setDateRangeAdjusted?: (value: boolean) => void;
  setSpeciesFilterMode?: (mode: 'all' | 'custom') => void;
}

interface OrderItem {
      id: string;
      label: string;
      type: 'order';
    }

interface SpeciesItem {
      id: string;
      label: string;
      parentOrder: string;
  type: 'species';
}

type FlattenedSpeciesItem = OrderItem | SpeciesItem;

const arraysMatch = (first: string[], second: string[]): boolean => {
  if (first.length !== second.length) {
    return false;
  }
  return first.every((value, index) => value === second[index]);
};

const sortByReference = (values: string[], reference: string[]): string[] => {
  if (values.length === 0) {
    return values;
  }
  const referenceOrder = new Map(reference.map((label, index) => [label, index]));
  return [...values].sort((a, b) => {
    const aIndex = referenceOrder.get(a) ?? Number.MAX_SAFE_INTEGER;
    const bIndex = referenceOrder.get(b) ?? Number.MAX_SAFE_INTEGER;
    return aIndex - bIndex;
  });
};

const toArray = (value: string | string[]): string[] => {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'string') {
    return value.split(',').filter((entry) => entry.length > 0);
  }
  return [];
};

const isOrderItem = (item: FlattenedSpeciesItem): item is OrderItem => item.type === 'order';
const isSpeciesItem = (item: FlattenedSpeciesItem): item is SpeciesItem => item.type === 'species';

const createOrderId = (label: string): string => `order_${label}`;
const createSpeciesId = (label: string): string => `species_${label}`;

export const StaticticsStickyNavbar = (props: StatisticsStickyNavbarProps) => {
  const {
    camera,
    cameraData,
    dateRange,
    dateValue,
    listOfSites,
    selectedOrders,
    selectedSpecies,
    setCamera,
    setDateRange,
    setDateValue,
    setSelectedOrders,
    setSelectedSpecies,
    setSite,
    showDownloadButtons = true,
    site,
    speciesList,
    updateCameraDetails,
    setDateRangeAdjusted,
    setSpeciesFilterMode,
  } = props;

  const [open, setOpen] = React.useState(false);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [isDownloadingCSV, setIsDownloadingCSV] = React.useState(false);

  const resolvedDateValue = String(dateValue ?? 1);

  const companyId = React.useMemo(() => {
    if (!listOfSites?.data || !site) {
      return null;
    }
    const numericSiteId = Number(site);
    const currentSite = listOfSites.data.find((siteOption) => siteOption.id === numericSiteId);
    return currentSite?.company_id ?? null;
  }, [listOfSites, site]);

  const numericCompanyId = companyId ?? 0;
  const { data: csvDownloadEnabled = false } = useFeatureFlag(
    numericCompanyId,
    'csv_download_enabled',
  );

  const flattenedSpeciesData = React.useMemo<FlattenedSpeciesItem[]>(() => {
    if (!speciesList?.length) {
      return [];
    }

    const flattened: FlattenedSpeciesItem[] = [];

    speciesList.forEach((order) => {
      if (!order.children?.length || !order.label) {
        return;
      }

      flattened.push({
        id: createOrderId(order.label),
        label: order.label,
        type: 'order',
      });

      order.children.forEach((species) => {
        if (!species.label) {
          return;
        }
        flattened.push({
          id: createSpeciesId(species.label),
          label: species.label,
          parentOrder: order.label,
          type: 'species',
        });
      });
    });

    return flattened;
  }, [speciesList]);

  const allOrderLabels = React.useMemo<string[]>(() => {
    return flattenedSpeciesData.filter(isOrderItem).map((item) => item.label);
  }, [flattenedSpeciesData]);

  const allSpeciesLabels = React.useMemo<string[]>(() => {
    return flattenedSpeciesData.filter(isSpeciesItem).map((item) => item.label);
  }, [flattenedSpeciesData]);

  const updateFilterMode = React.useCallback(
    (nextOrders: string[], nextSpecies: string[]) => {
      if (!setSpeciesFilterMode) {
        return;
      }
      const hasSpeciesData = allOrderLabels.length > 0 || allSpeciesLabels.length > 0;
      if (!hasSpeciesData) {
        setSpeciesFilterMode('all');
        return;
      }
      const ordersMatchAll =
        allOrderLabels.length === 0 ||
        arraysMatch(sortByReference(nextOrders, allOrderLabels), allOrderLabels);
      const speciesMatchAll =
        allSpeciesLabels.length === 0 ||
        arraysMatch(sortByReference(nextSpecies, allSpeciesLabels), allSpeciesLabels);
      setSpeciesFilterMode(ordersMatchAll && speciesMatchAll ? 'all' : 'custom');
    },
    [allOrderLabels, allSpeciesLabels, setSpeciesFilterMode],
  );

  const getOrderSpeciesLabels = React.useCallback(
    (orderLabel: string): string[] => {
      const orderEntry = speciesList?.find((entry) => entry.label === orderLabel);
      if (!orderEntry?.children?.length) {
        return [];
      }
      return orderEntry.children
        .map((child) => child.label)
        .filter((label): label is string => Boolean(label));
    },
    [speciesList],
  );

const speciesListLoaded = Array.isArray(props.speciesList);

  React.useEffect(() => {
    if (!speciesListLoaded) {
      return;
    }

    if (flattenedSpeciesData.length === 0) {
      if (selectedOrders.length > 0) {
        setSelectedOrders([]);
      }
      if (selectedSpecies.length > 0) {
        setSelectedSpecies([]);
      }
      updateFilterMode([], []);
      return;
    }

    const orderSet = new Set(allOrderLabels);
    const speciesSet = new Set(allSpeciesLabels);

    const sanitizedOrders = selectedOrders.filter((orderLabel) => orderSet.has(orderLabel));
    const sanitizedSpecies = selectedSpecies.filter((speciesLabel) => speciesSet.has(speciesLabel));

    if (!arraysMatch(sanitizedOrders, selectedOrders)) {
      setSelectedOrders(sanitizedOrders);
    }

    if (!arraysMatch(sanitizedSpecies, selectedSpecies)) {
      setSelectedSpecies(sanitizedSpecies);
    }

    let resolvedOrders = sanitizedOrders;
    let resolvedSpecies = sanitizedSpecies;

    if (sanitizedOrders.length === 0 && sanitizedSpecies.length === 0) {
      if (allOrderLabels.length > 0) {
        setSelectedOrders(allOrderLabels);
      }
      if (allSpeciesLabels.length > 0) {
        setSelectedSpecies(allSpeciesLabels);
      }
      resolvedOrders = allOrderLabels;
      resolvedSpecies = allSpeciesLabels;
    }

    updateFilterMode(resolvedOrders, resolvedSpecies);
  }, [
    allOrderLabels,
    allSpeciesLabels,
    flattenedSpeciesData.length,
    selectedOrders,
    selectedSpecies,
    speciesListLoaded,
    setSelectedOrders,
    setSelectedSpecies,
    updateFilterMode,
  ]);

  const updateOrderSelection = React.useCallback(
    (orderLabel: string, shouldSelect: boolean) => {
      const orderSpeciesLabels = getOrderSpeciesLabels(orderLabel);
      const nextOrdersSet = new Set(selectedOrders);
      const nextSpeciesSet = new Set(selectedSpecies);

      if (shouldSelect) {
        nextOrdersSet.add(orderLabel);
        orderSpeciesLabels.forEach((label) => nextSpeciesSet.add(label));
      } else {
        nextOrdersSet.delete(orderLabel);
        orderSpeciesLabels.forEach((label) => nextSpeciesSet.delete(label));
      }

      const nextOrders = sortByReference(Array.from(nextOrdersSet), allOrderLabels);
      const nextSpecies = sortByReference(Array.from(nextSpeciesSet), allSpeciesLabels);

      if (!arraysMatch(nextOrders, selectedOrders)) {
        setSelectedOrders(nextOrders);
      }
      if (!arraysMatch(nextSpecies, selectedSpecies)) {
        setSelectedSpecies(nextSpecies);
      }
      updateFilterMode(nextOrders, nextSpecies);
    },
    [
      allOrderLabels,
      allSpeciesLabels,
      getOrderSpeciesLabels,
      selectedOrders,
      selectedSpecies,
      setSelectedOrders,
      setSelectedSpecies,
      updateFilterMode,
    ],
  );

  const updateSpeciesSelection = React.useCallback(
    (speciesLabel: string, parentOrder: string | undefined, shouldSelect: boolean) => {
      const nextSpeciesSet = new Set(selectedSpecies);
      if (shouldSelect) {
        nextSpeciesSet.add(speciesLabel);
      } else {
        nextSpeciesSet.delete(speciesLabel);
      }

      const nextOrdersSet = new Set(selectedOrders);
      if (parentOrder) {
        const orderSpeciesLabels = getOrderSpeciesLabels(parentOrder);
        if (orderSpeciesLabels.length === 0) {
          if (shouldSelect) {
            nextOrdersSet.add(parentOrder);
          } else {
            nextOrdersSet.delete(parentOrder);
          }
        } else {
          const allSpeciesSelected = orderSpeciesLabels.every((label) => nextSpeciesSet.has(label));
          if (allSpeciesSelected) {
            nextOrdersSet.add(parentOrder);
          } else {
            nextOrdersSet.delete(parentOrder);
          }
        }
      }

      const nextOrders = sortByReference(Array.from(nextOrdersSet), allOrderLabels);
      const nextSpecies = sortByReference(Array.from(nextSpeciesSet), allSpeciesLabels);

      if (!arraysMatch(nextSpecies, selectedSpecies)) {
        setSelectedSpecies(nextSpecies);
      }
      if (!arraysMatch(nextOrders, selectedOrders)) {
        setSelectedOrders(nextOrders);
      }
      updateFilterMode(nextOrders, nextSpecies);
    },
    [
      allOrderLabels,
      allSpeciesLabels,
      getOrderSpeciesLabels,
      selectedOrders,
      selectedSpecies,
      setSelectedOrders,
      setSelectedSpecies,
      updateFilterMode,
    ],
  );

  const speciesSelectValue = React.useMemo(() => {
    const orderIds = selectedOrders.map((orderLabel) => createOrderId(orderLabel));
    const speciesIds = selectedSpecies.map((speciesLabel) => createSpeciesId(speciesLabel));
    return [...orderIds, ...speciesIds];
  }, [selectedOrders, selectedSpecies]);

  const handleSpeciesSelectChange = React.useCallback(
    (event: SelectChangeEvent<string[]>) => {
      const nextValue = toArray(event.target.value as unknown as string | string[]);
      const previousValue = speciesSelectValue;

      const addedId = nextValue.find((value) => !previousValue.includes(value));
      const removedId = previousValue.find((value) => !nextValue.includes(value));
      const changedId = addedId ?? removedId;
      if (!changedId) {
        return;
      }

      const changedItem = flattenedSpeciesData.find((item) => item.id === changedId);
      if (!changedItem) {
        return;
      }

      const shouldSelect = Boolean(addedId);
      if (isOrderItem(changedItem)) {
        updateOrderSelection(changedItem.label, shouldSelect);
      } else {
        updateSpeciesSelection(changedItem.label, changedItem.parentOrder, shouldSelect);
      }
    },
    [flattenedSpeciesData, speciesSelectValue, updateOrderSelection, updateSpeciesSelection],
  );

  const speciesSelectLabel = React.useMemo(() => {
    if (allSpeciesLabels.length === 0 && allOrderLabels.length === 0) {
      return 'No species available';
    }
    if (selectedSpecies.length === allSpeciesLabels.length && allSpeciesLabels.length > 0) {
      return 'All species';
    }
    if (selectedSpecies.length > 0 && selectedSpecies.length <= 2) {
      return selectedSpecies.join(', ');
    }
    if (selectedSpecies.length > 0) {
      return `${selectedSpecies.length} species`;
    }
    if (selectedOrders.length === allOrderLabels.length && allOrderLabels.length > 0) {
      return 'All species orders';
    }
    if (selectedOrders.length === 1) {
      return `${selectedOrders[0]} (all species)`;
    }
    if (selectedOrders.length > 1) {
      return `${selectedOrders.length} species orders`;
    }
    return 'Select species';
  }, [
    allOrderLabels.length,
    allSpeciesLabels.length,
    selectedOrders,
    selectedSpecies,
  ]);

  const truncatedSpeciesSelectLabel = React.useMemo(() => {
    const label = speciesSelectLabel;
    if (label.length <= 24) {
      return label;
    }
    return `${label.slice(0, 24)}…`;
  }, [speciesSelectLabel]);

  const isSpeciesSelectDisabled =
    flattenedSpeciesData.length === 0 && selectedOrders.length === 0 && selectedSpecies.length === 0;

  const openAdvancedPopup = React.useCallback(() => {
    setDateValue(3);
    setOpen(true);
  }, [setDateValue]);

  const handleClose = React.useCallback(() => {
    setOpen(false);
  }, []);

  const markDateRangeAdjusted = React.useCallback(() => {
    if (setDateRangeAdjusted) {
      setDateRangeAdjusted(true);
    }
  }, [setDateRangeAdjusted]);

  const applyDatePreset = React.useCallback(
    (preset: number) => {
      if (preset === 1) {
        const oneMonthBack = new Date();
        oneMonthBack.setMonth(oneMonthBack.getMonth() - 1);
        setDateRange({
          startDate: oneMonthBack,
          endDate: new Date(),
        });
        setDateValue(1);
        markDateRangeAdjusted();
        return;
      }

      if (preset === 2) {
        const threeMonthsBack = new Date();
        threeMonthsBack.setMonth(threeMonthsBack.getMonth() - 3);
        setDateRange({
          startDate: threeMonthsBack,
          endDate: new Date(),
        });
        setDateValue(2);
        markDateRangeAdjusted();
        return;
      }
    },
    [markDateRangeAdjusted, setDateRange, setDateValue],
  );

  const handleDateSelectChange = React.useCallback(
    (event: SelectChangeEvent) => {
      const nextValue = Number(event.target.value);
      if (Number.isNaN(nextValue)) {
        return;
      }
      if (nextValue === 3) {
        openAdvancedPopup();
        return;
      }
      applyDatePreset(nextValue);
    },
    [applyDatePreset, openAdvancedPopup],
  );

  const downloadReport = React.useCallback(async () => {
    if (isDownloading) {
      return;
    }

    try {
      setIsDownloading(true);

      const numericSiteId = Number(site);
      const siteDisplayName =
        listOfSites?.data?.find((siteOption) => siteOption.id === numericSiteId)?.site_display_name ??
        'Unknown Site';

      await exportMultipleChartsToPdf(siteDisplayName, dateRange.startDate, dateRange.endDate);
    } catch (error) {
      console.error('Error generating PDF report:', error);
      alert('Failed to generate PDF report. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  }, [dateRange.endDate, dateRange.startDate, isDownloading, listOfSites, site]);

  const downloadCSV = React.useCallback(async () => {
    if (isDownloadingCSV) {
      return;
    }

    try {
      setIsDownloadingCSV(true);

      const cameraIds = Array.isArray(camera) ? camera : [];

      const filterData = {
        start_date: dateRange.startDate.toISOString().split('T')[0],
        end_date: dateRange.endDate.toISOString().split('T')[0],
        site_id: site,
        camera_ids: cameraIds,
        species_order: selectedOrders,
        species_name: selectedSpecies,
      };

      const accessToken = JSON.parse(localStorage.getItem('accessToken') || '""') || null;
      const baseURL = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '';
      const response = await fetch(`${baseURL}/api/bird_tracking_csv_export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `JWT ${accessToken}`,
        },
        body: JSON.stringify(filterData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'bird_tracking_data.csv';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) {
          filename = match[1];
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes('401')) {
          alert('Authentication required. Please log in again.');
        } else if (error.message.includes('500')) {
          alert('Server error. Please try again later or contact support.');
        } else if (error.message.includes('400')) {
          alert('Invalid request. Please check your filters and try again.');
        } else {
          alert(`Failed to download CSV file: ${error.message}`);
        }
      } else {
        alert('Failed to download CSV file. Please try again.');
      }
    } finally {
      setIsDownloadingCSV(false);
    }
  }, [camera, dateRange.endDate, dateRange.startDate, isDownloadingCSV, selectedOrders, selectedSpecies, site]);

  const handleSiteChange = React.useCallback(
    (newSiteId: string) => {
    const previousSite = site;
    setSite(newSiteId);

    if (previousSite !== newSiteId) {
      if (setCamera) {
        setCamera([]);
      } else if (updateCameraDetails) {
        updateCameraDetails({ target: { value: [] } }, []);
      }
    }
    },
    [setCamera, setSite, site, updateCameraDetails],
  );

  React.useEffect(() => {
    if (cameraData?.data?.length && camera.length === 0) {
      const allCameraIds = cameraData.data.map((cameraOption) => cameraOption.id);
      if (setCamera) {
        setCamera(allCameraIds);
      } else if (updateCameraDetails) {
        updateCameraDetails({ target: { value: allCameraIds } }, allCameraIds);
      }
    }
  }, [camera, cameraData, setCamera, updateCameraDetails]);

  return (
    <Box
      sx={{
        position: 'sticky',
        zIndex: 100,
        top: 0,
        paddingTop: '25px',
        height: '100px',
        background: '#F9F7FC',
        display: 'flex',
        justifyContent: 'left',
      }}
    >
      <Box sx={{ width: '25%' }} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '12px',
          width: '1000px',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <SimpleDialog
          dateRange={dateRange}
          setDateRange={setDateRange}
          open={open}
          onClose={handleClose}
          onApply={markDateRangeAdjusted}
        />
        <FormControl>
          <Select
            value={resolvedDateValue}
            onChange={handleDateSelectChange}
            MenuProps={{
              PaperProps: {
                sx: {
                  borderRadius: '16px',
                  boxShadow: '0px 4px 20px rgba(23, 5, 45, 0.08)',
                  marginTop: '8px',
                  padding: '8px',
                  '& .MuiMenuItem-root': {
                    padding: '8px 12px',
                    fontSize: '14px',
                    borderRadius: '8px',
                    margin: '2px 0',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    '&.Mui-selected': {
                      backgroundColor: '#EDE9F5',
                    },
                  },
                },
              },
            }}
            sx={{
              height: '48px',
              minWidth: '200px',
              maxWidth: '200px',
              backgroundColor: '#FFFFFF',
              borderRadius: '22px',
              boxShadow: '0px 4px 10px rgba(23, 5, 45, 0.04)',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '& .MuiSelect-select': {
                padding: '10px 40px 10px 18px',
                textAlign: 'left',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                minWidth: 0,
                maxWidth: '100%',
                display: 'block',
              },
              '& .MuiSelect-icon': {
                right: '18px',
                width: '20px',
                height: '20px',
              },
            }}
          >
            <MenuItem value="1">Last 30 Days</MenuItem>
            <MenuItem value="2">Last 90 Days</MenuItem>
            <MenuItem value="3" onClick={openAdvancedPopup}>
              Advanced
            </MenuItem>
          </Select>
        </FormControl>
        <FormControl>
          <Select
            multiple
            displayEmpty
            value={speciesSelectValue}
            onChange={handleSpeciesSelectChange}
            disabled={isSpeciesSelectDisabled}
            renderValue={() => truncatedSpeciesSelectLabel}
            MenuProps={{
              PaperProps: {
                sx: {
                  borderRadius: '16px',
                  boxShadow: '0px 4px 20px rgba(23, 5, 45, 0.08)',
                  marginTop: '8px',
                  padding: '8px',
                  maxHeight: '320px',
                  '& .MuiMenuItem-root': {
                    padding: '6px 10px',
                    fontSize: '14px',
                    borderRadius: '8px',
                    margin: '2px 0',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    '&.Mui-selected': {
                      backgroundColor: '#EDE9F5',
                    },
                  },
                },
              },
            }}
            sx={{
              height: '48px',
              minWidth: '200px',
              maxWidth: '200px',
              backgroundColor: '#FFFFFF',
              borderRadius: '22px',
              boxShadow: '0px 4px 10px rgba(23, 5, 45, 0.04)',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '& .MuiSelect-select': {
                padding: '10px 40px 10px 18px',
                textAlign: 'left',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                minWidth: 0,
                maxWidth: '100%',
                display: 'block',
              },
              '& .MuiSelect-icon': {
                right: '18px',
                width: '20px',
                height: '20px',
              },
            }}
          >
            {flattenedSpeciesData.map((item) => {
              if (isOrderItem(item)) {
                const orderSpeciesLabels = getOrderSpeciesLabels(item.label);
                const selectedCount = orderSpeciesLabels.filter((label) =>
                  selectedSpecies.includes(label),
                ).length;
                const totalSpecies = orderSpeciesLabels.length;
                const isIndeterminate =
                  totalSpecies > 0 && selectedCount > 0 && selectedCount < totalSpecies;
                const isChecked =
                  selectedOrders.includes(item.label) ||
                  (totalSpecies > 0 && selectedCount === totalSpecies);

                return (
                  <MenuItem
                    key={item.id}
                    value={item.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      paddingLeft: '18px',
                      fontWeight: 600,
                    }}
                  >
                    <Checkbox size="small" checked={isChecked} indeterminate={isIndeterminate} />
                    {item.label}
                  </MenuItem>
                );
              }

              const isSpeciesSelected = selectedSpecies.includes(item.label);

              return (
                <MenuItem
                  key={item.id}
                  value={item.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    paddingLeft: '36px',
                    fontStyle: 'italic',
                    color: '#4A4A4A',
                  }}
                >
                  <Checkbox size="small" checked={isSpeciesSelected} />
                  {item.label}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        {listOfSites?.data ? (
          <FormControl>
            <Select
              value={site}
              onChange={(event) => handleSiteChange(event.target.value as string)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: '16px',
                    boxShadow: '0px 4px 20px rgba(23, 5, 45, 0.08)',
                    marginTop: '8px',
                    padding: '8px',
                    '& .MuiMenuItem-root': {
                      padding: '8px 12px',
                      fontSize: '14px',
                      borderRadius: '8px',
                      margin: '2px 0',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#EDE9F5',
                      },
                    },
                  },
                },
              }}
              sx={{
                height: '48px',
                minWidth: '200px',
                maxWidth: '200px',
                backgroundColor: '#FFFFFF',
                borderRadius: '22px',
                boxShadow: '0px 4px 10px rgba(23, 5, 45, 0.04)',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiSelect-select': {
                  padding: '10px 40px 10px 18px',
                  textAlign: 'left',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  minWidth: 0,
                  maxWidth: '100%',
                  display: 'block',
                },
                '& .MuiSelect-icon': {
                  right: '18px',
                  width: '20px',
                  height: '20px',
                },
              }}
            >
              {listOfSites.data.map((object, index) => (
                <MenuItem key={index} value={object?.id}>
                  {object?.site_display_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <FormControl>
            <Select
              value=""
              disabled
              displayEmpty
              sx={{
                height: '48px',
                minWidth: '200px',
                maxWidth: '200px',
                backgroundColor: '#FFFFFF',
                borderRadius: '22px',
                boxShadow: '0px 4px 10px rgba(23, 5, 45, 0.04)',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiSelect-select': {
                  padding: '10px 40px 10px 18px',
                  textAlign: 'left',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  minWidth: 0,
                  maxWidth: '100%',
                  display: 'block',
                  color: '#999999',
                },
                '& .MuiSelect-icon': {
                  right: '18px',
                  width: '20px',
                  height: '20px',
                },
              }}
            >
              <MenuItem value="">All sites</MenuItem>
            </Select>
          </FormControl>
        )}
        {cameraData?.data ? (
          <FormControl>
            <Select
              value={camera}
              multiple
              renderValue={(selected: number[]) => {
                if (selected.length === 0) {
                  return 'All cameras';
                }
                if (selected.length === 1) {
                  const cameraOption = cameraData.data?.find((cam) => cam.id === selected[0]);
                  return cameraOption?.camera_name || selected[0];
                }
                return `${selected.length} cameras`;
              }}
              onChange={(event) => {
                const nextValue = event.target.value as number[];
                if (updateCameraDetails) {
                  updateCameraDetails(event, nextValue);
                } else if (setCamera) {
                  setCamera(nextValue);
                }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: '16px',
                    boxShadow: '0px 4px 20px rgba(23, 5, 45, 0.08)',
                    marginTop: '8px',
                    maxHeight: '300px',
                    padding: '8px',
                    '& .MuiMenuItem-root': {
                      padding: '6px 10px',
                      fontSize: '14px',
                      borderRadius: '8px',
                      margin: '2px 0',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#EDE9F5',
                      },
                    },
                  },
                },
              }}
              sx={{
                height: '48px',
                minWidth: '200px',
                maxWidth: '200px',
                backgroundColor: '#FFFFFF',
                borderRadius: '22px',
                boxShadow: '0px 4px 10px rgba(23, 5, 45, 0.04)',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiSelect-select': {
                  padding: '10px 40px 10px 18px',
                  textAlign: 'left',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  minWidth: 0,
                  maxWidth: '100%',
                  display: 'block',
                },
                '& .MuiSelect-icon': {
                  right: '18px',
                  width: '20px',
                  height: '20px',
                },
              }}
            >
              {cameraData.data.map((object, index) => (
                <MenuItem key={index} value={object?.id}>
                  <Checkbox checked={camera.includes(object?.id)} />
                  {object?.camera_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <FormControl>
            <Select
              value={[]}
              multiple
              disabled
              displayEmpty
              renderValue={() => 'All cameras'}
              sx={{
                height: '48px',
                minWidth: '200px',
                maxWidth: '200px',
                backgroundColor: '#FFFFFF',
                borderRadius: '22px',
                boxShadow: '0px 4px 10px rgba(23, 5, 45, 0.04)',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiSelect-select': {
                  padding: '10px 40px 10px 18px',
                  textAlign: 'left',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  minWidth: 0,
                  maxWidth: '100%',
                  display: 'block',
                  color: '#999999',
                },
                '& .MuiSelect-icon': {
                  right: '18px',
                  width: '20px',
                  height: '20px',
                },
              }}
            >
              <MenuItem value="">All cameras</MenuItem>
            </Select>
          </FormControl>
        )}
        {showDownloadButtons && (
          <>
            <Tooltip
              title={isDownloading ? 'Generating PDF report...' : 'Download PDF report'}
              arrow
              placement="top"
            >
              <span>
                <IconButton
                  aria-label={isDownloading ? 'Generating PDF report...' : 'Download PDF report'}
                  onClick={downloadReport}
                  disabled={isDownloading}
                  sx={{
                    backgroundColor: isDownloading ? '#f5f5f5' : 'white',
                    color: isDownloading ? '#666666' : '#333333',
                    width: '48px',
                    height: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    border: '1px solid #dddddd',
                    boxShadow: isDownloading ? 'none' : '0 1px 3px rgba(0,0,0,0.12)',
                    cursor: isDownloading ? 'not-allowed' : 'pointer',
                    opacity: 1,
                    '&:hover': {
                      backgroundColor: isDownloading ? '#f5f5f5' : '#f8f8f8',
                    },
                  }}
                >
                  {isDownloading ? (
                    <CircularProgress size={20} style={{ color: '#666666' }} />
                  ) : (
                    <GetAppIcon fontSize="medium" style={{ color: '#333333' }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            {csvDownloadEnabled && (
              <Tooltip
                title={isDownloadingCSV ? 'Generating CSV report...' : 'Download CSV report'}
                arrow
                placement="top"
              >
                <span>
                  <IconButton
                    aria-label={
                      isDownloadingCSV ? 'Generating CSV report...' : 'Download CSV report'
                    }
                    onClick={downloadCSV}
                    disabled={isDownloadingCSV}
                    sx={{
                      backgroundColor: isDownloadingCSV ? '#f5f5f5' : 'white',
                      color: isDownloadingCSV ? '#666666' : '#333333',
                      width: '48px',
                      height: '48px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      border: '1px solid #dddddd',
                      boxShadow: isDownloadingCSV ? 'none' : '0 1px 3px rgba(0,0,0,0.12)',
                      cursor: isDownloadingCSV ? 'not-allowed' : 'pointer',
                      opacity: 1,
                      '&:hover': {
                        backgroundColor: isDownloadingCSV ? '#f5f5f5' : '#f8f8f8',
                      },
                    }}
                  >
                    {isDownloadingCSV ? (
                      <CircularProgress size={20} style={{ color: '#666666' }} />
                    ) : (
                      <TableViewIcon fontSize="medium" style={{ color: '#333333' }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

